import { all, takeEvery, fork, call, put } from 'redux-saga/effects';
import axios from 'axios';
import isFunction from 'lodash/isFunction';

import actions from './actions';
import { handleError } from '../../errorHandler';
import { API_HOST } from '../../constants';

const MISSION_API_URL = `${API_HOST}/api/mission`;
const MISSION_TRANSACTION_RECORD_API_URL = `${MISSION_API_URL}/missionTransactionRecord`;
const MILESTONE_REWARD_TRANSACTION_RECORD_API_URL = `${MISSION_API_URL}/milestoneRewardTransactionRecord`;
const SUB_MISSION_TRANSACTION_RECORD_API_URL = `${MISSION_API_URL}/subMissionCompletionRecord`;
const THIRD_PARTY_MISSION_API_URL = `${API_HOST}/api/mission/externalSubMissionType`;
const MISSION_MUTATION_REQUEST_CONFIG = { timeout: 300000 };

function* createMission() {
  yield takeEvery(actions.CREATE_MISSION, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'post'],
        MISSION_API_URL,
        formData,
        MISSION_MUTATION_REQUEST_CONFIG,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({ type: actions.CREATE_MISSION_ENDED });
    }
  });
}

function* createThirdPartyMission() {
  yield takeEvery(actions.CREATE_THIRD_PARTY_MISSION, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'post'], THIRD_PARTY_MISSION_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({ type: actions.CREATE_THIRD_PARTY_MISSION_ENDED });
    }
  });
}

function* findMission() {
  yield takeEvery(actions.FIND_MISSION, function* (action) {
    try {
      const { id, onSuccess } = action;
      const res = yield call([axios, 'get'], `${MISSION_API_URL}/${id}`);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.FIND_MISSION_ENDED });
    }
  });
}

function* searchMissions() {
  yield takeEvery(actions.SEARCH_MISSIONS, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call([axios, 'get'], `${MISSION_API_URL}?${parameters.toString()}`);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({ type: actions.SEARCH_MISSIONS_ENDED });
    }
  });
}

function* searchThirdPartyMissions() {
  yield takeEvery(actions.SEARCH_THIRD_PARTY_SUB_MISSIONS, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${THIRD_PARTY_MISSION_API_URL}?${parameters.toString()}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({ type: actions.SEARCH_THIRD_PARTY_SUB_MISSIONS_ENDED });
    }
  });
}

function* updateMission() {
  yield takeEvery(actions.UPDATE_MISSION, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'put'],
        MISSION_API_URL,
        formData,
        MISSION_MUTATION_REQUEST_CONFIG,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({ type: actions.UPDATE_MISSION_ENDED });
    }
  });
}

function* updateThirdPartyMission() {
  yield takeEvery(actions.UPDATE_THIRD_PARTY_MISSION, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'put'], THIRD_PARTY_MISSION_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({ type: actions.UPDATE_THIRD_PARTY_MISSION_ENDED });
    }
  });
}

function* deleteMission() {
  yield takeEvery(actions.DELETE_MISSION, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'delete'], `${MISSION_API_URL}`, { data: formData });
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({ type: actions.DELETE_MISSION_ENDED });
    }
  });
}

function* deleteThirdPartyMission() {
  yield takeEvery(actions.DELETE_THIRD_PARTY_MISSION, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'delete'], `${THIRD_PARTY_MISSION_API_URL}`, {
        data: formData,
      });
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({ type: actions.DELETE_THIRD_PARTY_MISSION_ENDED });
    }
  });
}

function* searchSubMissionRecords() {
  yield takeEvery(actions.SEARCH_SUB_MISSION_TRANSACTION_RECORD, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${SUB_MISSION_TRANSACTION_RECORD_API_URL}?${parameters.toString()}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({ type: actions.SEARCH_SUB_MISSION_TRANSACTION_RECORD_ENDED });
    }
  });
}

function* findSubMissionRecord() {
  yield takeEvery(actions.FIND_SUB_MISSION_TRANSACTION_RECORD, function* (action) {
    try {
      const { id, onSuccess } = action;
      const res = yield call([axios, 'get'], `${SUB_MISSION_TRANSACTION_RECORD_API_URL}/${id}`);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.FIND_SUB_MISSION_TRANSACTION_RECORD_ENDED });
    }
  });
}

function* searchMissionTransactionRecords() {
  yield takeEvery(actions.SEARCH_MISSION_TRANSACTION_RECORD, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${MISSION_TRANSACTION_RECORD_API_URL}?${parameters.toString()}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({ type: actions.SEARCH_MISSION_TRANSACTION_RECORD_ENDED });
    }
  });
}

function* searchMilestoneRewardTransactionRecords() {
  yield takeEvery(actions.SEARCH_MILESTONE_REWARD_TRANSACTION_RECORD, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${MILESTONE_REWARD_TRANSACTION_RECORD_API_URL}?${parameters.toString()}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({ type: actions.SEARCH_MILESTONE_REWARD_TRANSACTION_RECORD_ENDED });
    }
  });
}

function* findMilestoneRewardTransactionRecord() {
  yield takeEvery(actions.FIND_MILESTONE_REWARD_TRANSACTION_RECORD, function* (action) {
    try {
      const { id, onSuccess } = action;
      const res = yield call(
        [axios, 'get'],
        `${MILESTONE_REWARD_TRANSACTION_RECORD_API_URL}/${id}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.FIND_MILESTONE_REWARD_TRANSACTION_RECORD_ENDED });
    }
  });
}
function* findMissionTransactionRecord() {
  yield takeEvery(actions.FIND_MISSION_TRANSACTION_RECORD, function* (action) {
    try {
      const { id, onSuccess } = action;
      const res = yield call([axios, 'get'], `${MISSION_TRANSACTION_RECORD_API_URL}/${id}`);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.FIND_MISSION_TRANSACTION_RECORD_ENDED });
    }
  });
}

function* searchMissionCategories() {
  yield takeEvery(actions.SEARCH_MISSION_CATEGORIES, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${MISSION_API_URL}/missionCategory?${parameters.toString()}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_MISSION_CATEGORIES_END });
    }
  });
}

function* createMissionCategory() {
  yield takeEvery(actions.CREATE_MISSION_CATEGORY, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'post'], `${MISSION_API_URL}/missionCategory`, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.CREATE_MISSION_CATEGORY_END });
    }
  });
}

function* updateMissionCategory() {
  yield takeEvery(actions.UPDATE_MISSION_CATEGORY, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'put'], `${MISSION_API_URL}/missionCategory`, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.UPDATE_MISSION_CATEGORY_ENDED });
    }
  });
}

function* deleteMissionCategories() {
  yield takeEvery(actions.DELETE_MISSION_CATEGORIES, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'delete'], `${MISSION_API_URL}/missionCategory`, {
        data: formData,
      });
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.DELETE_MISSION_CATEGORIES_ENDED });
    }
  });
}

function* searchSubMissionDetails() {
  yield takeEvery(actions.SEARCH_SUB_MISSION_DETAILS, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${MISSION_API_URL}/report/subMissionDetails?${parameters.toString()}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_SUB_MISSION_DETAILS_ENDED });
    }
  });
}

function* searchMissionsWithProgress() {
  yield takeEvery(actions.SEARCH_MISSIONS_WITH_PROGRESS, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const {
        data: { data: missionData },
      } = yield call([axios, 'get'], `${MISSION_API_URL}?${parameters.toString()}`);
      const firstMission = missionData[0];
      const progressParameters = new URLSearchParams({
        count_from: firstMission.mission_starts_at,
        count_to: firstMission.mission_ends_at,
        mission_id: firstMission.id,
      });

      const {
        data: { data: progressData },
      } = yield call(
        [axios, 'get'],
        `${MISSION_API_URL}/report/missionProgress?${progressParameters.toString()}`,
      );

      const data = { firstMission, progressData, missionData };
      if (isFunction(onSuccess)) {
        yield call(onSuccess, data);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({ type: actions.SEARCH_MISSIONS_WITH_PROGRESS_ENDED });
    }
  });
}

function* searchMissionProgress() {
  yield takeEvery(actions.SEARCH_MISSION_PROGRESS, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const { data } = yield call(
        [axios, 'get'],
        `${MISSION_API_URL}/report/missionProgress?${parameters.toString()}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_MISSION_PROGRESS_ENDED });
    }
  });
}

export default function* rootSaga() {
  yield all([
    fork(createMission),
    fork(createThirdPartyMission),
    fork(findMission),
    fork(searchMissions),
    fork(searchThirdPartyMissions),
    fork(updateMission),
    fork(updateThirdPartyMission),
    fork(deleteMission),
    fork(deleteThirdPartyMission),
    fork(findSubMissionRecord),
    fork(findMilestoneRewardTransactionRecord),
    fork(findMissionTransactionRecord),
    fork(searchSubMissionRecords),
    fork(searchMissionTransactionRecords),
    fork(searchMilestoneRewardTransactionRecords),
    fork(searchMissionCategories),
    fork(searchSubMissionDetails),
    fork(searchMissionProgress),
    fork(createMissionCategory),
    fork(updateMissionCategory),
    fork(deleteMissionCategories),
    fork(searchMissionsWithProgress),
  ]);
}
